"use client";
import "./bangkeo.scss";
import dayjs from 'dayjs'; // Core Day.js
import utc from 'dayjs/plugin/utc.js'; // UTC plugin
import timezone from 'dayjs/plugin/timezone.js'; // Timezone plugin
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isNextBuild } from "@/lib/target";
import { BangkeoService } from "../../../services/bangkeoService";
import { ALL, FAVORITE, LEAGUES_PLUGINS, LIVE } from "../../../contants";
import {
  SET_SEARCH_TYPE,
  SET_HOT_LEAGUE_SEARCH,
  SET_DATE_SELECTED,
  SET_SEARCH_TEXT_VALUE,
  SET_FILTER_LEAGUES,
  SET_FILTER_RESULTS,
} from "@/lib/redux/reducers/actionTypes";
import {
  SEARCH_TYPE_COUNTRY,
  SEARCH_TYPE_LEAGUE,
  SEARCH_TYPE_LEAGUES,
  SEARCH_TYPE_TEAM,
  SEARCH_TYPE_TEAMS,
  SEARCH_HOT_LEAGUE_DEFAULT,
} from "@/contants";
import { CHANGE_BOOKMAKER } from "@/lib/redux/reducers/actionTypes";
import { convertToLeagueMatchFormat } from "@/utils/convertToLeagueMatchFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

import LoadingInner from "@/app/loadingInner";

import BangkeoHeader from "@/components/client/BangkeoHeader";
import BangkeoFavorite from "@/components/client/BangkeoFavorite";
import BangkeoAllMatches from "@/components/client/BangkeoAllMatches";
import BangkeoLiveMatches from "@/components/client/BangkeoLiveMatches";
import BangkeoHotMatches from "@/components/client/BangkeoHotMatches";

const Bangkeo = ({ leagueAttr }) => {
  const dispatch = useDispatch();

  // Memoize selector values to prevent unnecessary re-renders
  const currentRoute = useSelector(
    useCallback((state) => state.fixtureRouteReducer.currentRoute, [])
  );
  const dateSelected = useSelector(
    useCallback(
      (state) => state.searchReducer.dateSelected || dayjs().format("DD-MM-YYYY"),
      []
    )
  );
  const searchType = useSelector(
    useCallback(
      (state) => state.searchReducer.searchType || SEARCH_TYPE_TEAM,
      []
    )
  );
  const searchTextValue = useSelector(
    useCallback(
      (state) => state.searchReducer.searchTextValue || "",
      []
    )
  );
  const hotLeagueSearch = useSelector(
    useCallback(
      (state) => state.searchReducer.hotLeagueSearch || SEARCH_HOT_LEAGUE_DEFAULT,
      []
    )
  );
  const companyIdOdds = useSelector(
    useCallback((state) => state.bookmakerReducer, [])
  );

  // Move state validation to a separate memoized function
  const validateDateSelected = useMemo(() => {
    const current = dayjs();
    const inpDate = dayjs(dateSelected, "DD-MM-YYYY");
    return !inpDate.isValid() || current.isAfter(inpDate, "day");
  }, [dateSelected]);

  // Initialize state only once on mount
  useEffect(() => {
    if (validateDateSelected) {
      dispatch({
        type: SET_DATE_SELECTED,
        payload: dayjs().format("DD-MM-YYYY"),
      });
    }
    dispatch({ type: SET_SEARCH_TYPE, payload: searchType });
    dispatch({ type: SET_SEARCH_TEXT_VALUE, payload: searchTextValue });
    dispatch({ type: SET_HOT_LEAGUE_SEARCH, payload: hotLeagueSearch });
  }, []);

  // Memoize API filter creation
  const createApiFilter = useCallback(() => {
    let dataFilter = "";
    let dataFilterInclude = `date:${dayjs(dateSelected, "DD-MM-YYYY").format(
      "YYYY-MM-DD"
    )},`;

    if (searchType === SEARCH_TYPE_COUNTRY) {
      dataFilter += `countryId:${searchTextValue},`;
    }

    switch (searchType) {
      case SEARCH_TYPE_LEAGUE:
        dataFilterInclude += `leagueId:${searchTextValue},`;
        break;
      case SEARCH_TYPE_LEAGUES:
        dataFilterInclude += `leagueIds:${searchTextValue},`;
        break;
      case SEARCH_TYPE_TEAM:
        dataFilterInclude += `teamId:${searchTextValue},`;
        break;
      case SEARCH_TYPE_TEAMS:
        dataFilterInclude += `teamIds:${searchTextValue},`;
        break;
    }

    return { dataFilter, dataFilterInclude };
  }, [dateSelected, searchType, searchTextValue]);

  // Memoize the setBookerMarker callback
  const setBookerMarker = useCallback(
    (bookmarker) => {
      dispatch({ type: CHANGE_BOOKMAKER, payload: bookmarker });
    },
    [dispatch]
  );

  // State và các biến từ Redux
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchText = useSelector((state) => state.searchReducer.searchText);

  useEffect(() => {
    fetchLeaguesMatchesAll(companyIdOdds);
  }, [
    dateSelected,
    searchTextValue,
    searchType,
    hotLeagueSearch,
    companyIdOdds,
  ]);
  
  useEffect(() => {
    fetchFilterLeagues();
  }, [dateSelected]);

  const fetchLeaguesMatchesAll = useCallback(
    async (companyIdOdds) => {
      try {
        setLoading(true);

        // Cache cục bộ để tối ưu gọi API
        const cache =
          fetchLeaguesMatchesAll.cache ||
          (fetchLeaguesMatchesAll.cache = new Map());
        const cacheKey = JSON.stringify({
          dateSelected,
          searchTextValue,
          searchType,
          companyIdOdds,
          leagueAttr,
        });

        if (cache.has(cacheKey)) {
          setMatches(cache.get(cacheKey));
          setLoading(false);
          return;
        }

        const { dataFilter, dataFilterInclude } = createApiFilter();

        const data = await BangkeoService.fetchLeaguesMatchesFull({
          search: "",
          filter: dataFilter,
          fields: "",
          include: "homeTeam,awayTeam,odds,stats,events,league",
          searchInclude: "",
          filterInclude: dataFilterInclude,
          fieldsInclude: "",
          filterOdds: isNextBuild ? companyIdOdds : -1,
          page: 1,
          limit: 10000,
        });

        let matchData = data?.data?.data || [];
        matchData = matchData.filter((item) => Number(item.status) !== -1);

        // Lưu cache và cập nhật state
        cache.set(cacheKey, matchData);
        setMatches(matchData);
        dispatch({
          type: SET_FILTER_RESULTS,
          payload: convertToLeagueMatchFormat(matchData),
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching leagues matches:", error);
        setLoading(false);
      }
    },
    [dateSelected, searchTextValue, searchType, leagueAttr]
  );

  const fetchFilterLeagues = useCallback(async () => {
    try {
      setLoading(true);
      const dataFilterInclude = `date:${dayjs(
        dateSelected,
        "DD-MM-YYYY"
      ).format("YYYY-MM-DD")}`;
      const data = await BangkeoService.fetchLeaguesMatchesFull({
        fields: "id,name,country,countryId",
        filterInclude: dataFilterInclude,
        include: "homeTeam,awayTeam,odds,league",
        filterOdds: isNextBuild ? companyIdOdds : -1,
        page: 1,
        limit: 100,
      });
      let leaguesData = data?.data?.data || [];
      if (!isNextBuild) {
        leaguesData = leaguesData.filter((match) => LEAGUES_PLUGINS.map((league) => league.id).includes(+match?.leagueId));
        leaguesData = leaguesData.filter((match) => match.handicapOdds?.length > 0 || match.overUnderOdds?.length > 0);

        if (leagueAttr !== "") {
          leaguesData = leaguesData.filter((item) => +item.id === +leagueAttr);
        }
      }
      dispatch({
        type: SET_FILTER_LEAGUES,
        payload: convertToLeagueMatchFormat(leaguesData),
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leagues:", error);
      setLoading(false);
    }
  });

  const memoizedMatches = useMemo(() => matches, [matches]);

  // Memoize the rendered content
  const renderContent = useMemo(() => {
    if (loading) {
      return (
        <div className="bangkeo rounded-lg mb-4 relative min-h-[5000px] lg:min-h-[10px]">
          <LoadingInner />
        </div>
      );
    }

    return (
      <div className="bangkeo rounded-lg mb-4 relative">
        <BangkeoHeader onBookMakerChange={setBookerMarker} />
        {currentRoute === ALL && <BangkeoAllMatches matches={memoizedMatches} />}
        {currentRoute === LIVE && (
          <BangkeoLiveMatches matches={memoizedMatches} />
        )}
        {currentRoute === SEARCH_HOT_LEAGUE_DEFAULT && (
          <BangkeoHotMatches matches={memoizedMatches} />
        )}
        {currentRoute === FAVORITE && <BangkeoFavorite />}
      </div>
    );
  }, [loading, currentRoute, memoizedMatches, setBookerMarker]);

  return renderContent;
};

export default Bangkeo;